// Pure scene math for the home page hero animation (components/hero/QuantaField.jsx): tilted
// orbital rings around the app icons, "quanta" particles riding them in perspective, and pulses
// travelling between the apps. No DOM here, so it is unit-tested (tests/quanta-orbit.test.mjs).

export const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
export const easeOutCubic = (t) => 1 - Math.pow(1 - clamp(t), 3);

// Small seeded PRNG so the scene is identical on every load (and in tests).
export function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Three rings at different tilts read as one atom-like system rather than three flat circles.
export const RINGS = [
  { radius: 0.62, tilt: 1.12, yaw: -0.42, spin: 0.11 },
  { radius: 0.86, tilt: 1.22, yaw: 0.52, spin: -0.08 },
  { radius: 1.08, tilt: 1.3, yaw: 0.06, spin: 0.05 },
];

export function makeQuanta(count, seed = 7, rings = RINGS) {
  const rand = mulberry32(seed);
  return Array.from({ length: count }, (_, i) => {
    const ring = i % rings.length;
    const dir = rings[ring].spin < 0 ? -1 : 1;
    return {
      ring,
      phase: rand() * Math.PI * 2,
      speed: dir * (0.12 + rand() * 0.22),
      size: 1 + rand() * 1.6,
      hue: rand() < 0.28 ? "blue" : "violet",
      delay: rand() * 0.6,
    };
  });
}

// A point on a ring: a circle of radius ring.radius * R, tilted away from the viewer by
// ring.tilt (+ pointer tilt), then turned in the screen plane by ring.yaw (+ pointer yaw).
// Returns screen x/y, depth z (positive = behind the centre) and a perspective scale.
export function ringPoint(ring, angle, { cx, cy, R, tiltOffset = 0, yawOffset = 0 }) {
  const r = ring.radius * R;
  const tilt = ring.tilt + tiltOffset;
  const yaw = ring.yaw + yawOffset;
  const x0 = r * Math.cos(angle);
  const y1 = r * Math.sin(angle) * Math.cos(tilt);
  const z = r * Math.sin(angle) * Math.sin(tilt);
  const focal = 3 * R;
  const scale = focal / (focal + z);
  const xr = x0 * Math.cos(yaw) - y1 * Math.sin(yaw);
  const yr = x0 * Math.sin(yaw) + y1 * Math.cos(yaw);
  return { x: cx + xr * scale, y: cy + yr * scale, z, scale };
}

// Quadratic Bezier between two app icons, bowed towards the centre of the scene.
export function linkControl(a, b, center, bend = 0.35) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  return { x: mx + (center.x - mx) * bend, y: my + (center.y - my) * bend };
}

export function bezierPoint(p0, p1, p2, t) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  };
}

// Every pair of apps, once: three apps give three links.
export function appLinks(points) {
  const links = [];
  for (let i = 0; i < points.length; i++) for (let j = i + 1; j < points.length; j++) links.push([i, j]);
  return links;
}
